import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useQuery, useMutation } from "@apollo/client";

import { pdf } from "@react-pdf/renderer";

import { SEND_EMAIL } from "../../../../../Mutations/Orders";

import { LEGAL_DATA_FOR_INVOICE, WORKING_HOURS } from "../../../../../Queries/Contacts";

import { MANAGER } from "../../../../../Queries/Managers";

import Fieldset from "../../../Global/Forms/Fieldset";

import CenterLoader from "../../../Loaders/CenterLoader";

import DocumentInvoice from "../../Documents/Invoice";

/**
 * Load a remote image as a data URL so react-pdf embeds it reliably.
 */
const loadImageAsDataUrl = async ( src ) => {

    if ( ! src || typeof src !== "string" ) return null;

    if ( src.startsWith ( "data:" ) ) return src;

    try {

        const response = await fetch ( src, { mode: "cors", credentials: "omit" } );

        if ( ! response.ok ) return null;

        const blob = await response.blob ();

        return await new Promise ( ( resolve ) => {

            const reader = new FileReader ();

            reader.onloadend = () => resolve ( reader.result || null );

            reader.onerror = () => resolve ( null );

            reader.readAsDataURL ( blob );

        } );

    } catch {

        // Fallback: wait until the browser has the image decoded
        return await new Promise ( ( resolve ) => {

            const img = new window.Image ();

            img.crossOrigin = "anonymous";

            img.onload = () => resolve ( src );

            img.onerror = () => resolve ( null );

            img.src = src;

        } );

    }

};

/**
 * Clone order with product photos preloaded as data URLs.
 */
const orderWithPreloadedPhotos = async ( order ) => {

    const vendors = order?.orderProductVendors || [];

    if ( ! vendors.length ) return order;

    const orderProductVendors = await Promise.all (

        vendors.map ( async ( item ) => {

            const dataUrl = await loadImageAsDataUrl ( item?.product?.currentImage );

            if ( ! dataUrl || ! item?.product ) return item;

            return {

                ...item,

                product: {

                    ...item.product,

                    currentImage: dataUrl

                }

            };

        } )

    );

    return { ...order, orderProductVendors };

};

export default function PDFGeneratorWidget ( { state, order } ) { const

    { t : __ } = useTranslation (),

    [ generating, setGenerating ] = useState ( false ),

    { data: legalData, loading: loadingLegalData } = useQuery ( LEGAL_DATA_FOR_INVOICE, {
        variables: { countryCode: order?.countryCode || state?.countryCode || null }
    } ),

    { data: workingHoursData, loading: loadingWorkingHours } = useQuery ( WORKING_HOURS ),

    { data: manager, loading: loadingManager } = useQuery ( MANAGER, { variables: { id: order?.userId }, fetchPolicy: "network-only" } ),

    [ sendEmail, { loading : loadingMailer } ] = useMutation ( SEND_EMAIL ),

    buildDocument = ( type, orderData ) => (

        <DocumentInvoice

            type={ type }

            state={ state }

            order={ orderData }

            legalData={ legalData?.legalData }

            hours={ workingHoursData?.workingOnDays || {} }

            manager={ manager }

        />

    ),

    /**
     *
     * @param { Object } e
     *
     * @param { string } type
     *
     * @description generate pdf document (waits for product photos on offer)
     *
     * @returns pdf document
     *
     */

    generatePDF = async ( e, type ) => {

        e.preventDefault ();

        setGenerating ( true );

        try {

            const orderData = type === "offer"
                ? await orderWithPreloadedPhotos ( order )
                : order;

            const blob = await pdf ( buildDocument ( type, orderData ) ).toBlob (),

                url = window.URL.createObjectURL ( blob );

            window.open ( url, '_blank' );

        } finally {

            setGenerating ( false );

        }

    },

    sendDocument = async ( e, documentData ) => { e.preventDefault ();

        const blob = await pdf ( documentData ).toBlob ();

        sendEmail ( { variables: { orderId: parseInt ( order.id ), orderType: order.orderType, pdf: new File ( [ blob ], "invoice.pdf" ) } } );

    };

    /** @returns { JSX.Element } PDFGeneratorWidget */

    return (

        <Fieldset legend="PDF">

            {

                ( loadingLegalData || loadingWorkingHours || loadingManager || generating ) ? <CenterLoader /> :

                <div className="document_generation_actions">

                    <button className="block width_full"

                        onClick={ e => generatePDF ( e, "invoice" ) }>

                            { __ ( "download_invoice" ) }

                    </button>

                    <button className="block width_full"

                        onClick={ e => generatePDF ( e, "receipt" ) }>

                            { __ ( "download_receipt" ) }

                    </button>

                    <button className="block width_full"

                        onClick={ e => generatePDF ( e, "offer" ) }>

                            { __ ( "download_offer" ) }

                    </button>

                </div>

            }

        </Fieldset>

    );

};
